import { validEmail } from "../../../../../utils/validation";
import {
  COORDINATE_MAX,
  COORDINATE_MIN,
  defaultESignErrorState,
  ESignErrorState,
  SIGNER_EMAIL_MAX_LENGTH,
  SIGNER_NAME_MAX_LENGTH,
  SIGNER_NAME_MIN_LENGTH,
} from "./esign.constants";
import { SIGN_TYPE, SignTypeValue } from "./esign.enums";

type AadhaarSignType = "AADHAAR";
type SupportedValidationSignType = SignTypeValue | AadhaarSignType;

export function validPhoneWithCountryCode(raw: string): boolean {
  const value = String(raw || "").trim();
  if (!value) return false;

  const digits = value.replace(/\D/g, "");
  if (/^[6-9]\d{9}$/.test(digits)) return true;
  return /^91[6-9]\d{9}$/.test(digits);
}

export function normalizePhone(raw: string): string {
  const value = String(raw || "").trim();
  const digits = value.replace(/\D/g, "");
  if (/^[6-9]\d{9}$/.test(digits)) return `+91${digits}`;
  if (/^91[6-9]\d{9}$/.test(digits)) return `+${digits}`;
  return digits ? `+${digits}` : "";
}

export function hasMeaningfulPhoneInput(raw: string): boolean {
  const digits = String(raw || "")
    .trim()
    .replace(/\D/g, "");

  // react-phone-input keeps country code (like "91") even when user clears input.
  // Treat country-code-only as empty for optional-number validations.
  if (!digits || digits === "91") return false;
  return true;
}

export function isValidCoordinate(raw: string): boolean {
  if (!/^\d+$/.test(raw)) return false;
  const numeric = Number(raw);
  return (
    Number.isInteger(numeric) &&
    numeric >= COORDINATE_MIN &&
    numeric <= COORDINATE_MAX
  );
}

export function isValidName(nameVal: string): boolean {
  const pattern = /[^a-zA-Z\s]/g;
  if (pattern.test(nameVal)) return false;
  if (!nameVal.replace(/\s/g, "").length) return false;
  if (
    nameVal.length < SIGNER_NAME_MIN_LENGTH ||
    nameVal.length > SIGNER_NAME_MAX_LENGTH
  ) {
    return false;
  }
  return true;
}

type ValidateESignFormInput = {
  signType: SupportedValidationSignType;
  signerName: string;
  signerEmail: string;
  signerWhatsappNumber: string;
  statex: string;
  statey: string;
};

function isAadhaarSignType(signType: SupportedValidationSignType): boolean {
  return signType === SIGN_TYPE.AADHAAR_OTP || signType === "AADHAAR";
}

export function validateESignFormInput({
  signType,
  signerName,
  signerEmail,
  signerWhatsappNumber,
  statex,
  statey,
}: ValidateESignFormInput): { isValid: boolean; error: ESignErrorState } {
  const error = { ...defaultESignErrorState };

  if (!isValidName(signerName)) {
    error.nameError = true;
    return { isValid: false, error };
  }

  if (!isValidCoordinate(statex)) {
    error.variableXError = true;
    return { isValid: false, error };
  }

  if (!isValidCoordinate(statey)) {
    error.variableYError = true;
    return { isValid: false, error };
  }

  if (signType === SIGN_TYPE.EMAIL) {
    if (
      signerEmail.length > SIGNER_EMAIL_MAX_LENGTH ||
      !validEmail(signerEmail)
    ) {
      error.emailError = true;
      return { isValid: false, error };
    }
    return { isValid: true, error };
  }

  if (signType === SIGN_TYPE.WHATSAPP) {
    if (!validPhoneWithCountryCode(signerWhatsappNumber)) {
      error.phoneError = true;
      return { isValid: false, error };
    }
    return { isValid: true, error };
  }

  if (!isAadhaarSignType(signType)) {
    return { isValid: true, error };
  }

  const hasEmail = signerEmail.length > 0;
  const hasPhone = hasMeaningfulPhoneInput(signerWhatsappNumber);

  if (!hasEmail && !hasPhone) {
    error.contactEitherOrError = true;
    return { isValid: false, error };
  }

  if (hasEmail && (signerEmail.length > SIGNER_EMAIL_MAX_LENGTH || !validEmail(signerEmail))) {
    error.emailError = true;
    return { isValid: false, error };
  }

  if (hasPhone && !validPhoneWithCountryCode(signerWhatsappNumber)) {
    error.phoneError = true;
    return { isValid: false, error };
  }

  return { isValid: true, error };
}
